/**
 * CommandPalette Component
 *
 * Spotlight-style overlay opened by Cmd+K.
 * Searches navigation commands and provides quick actions.
 *
 * Uses cmdk for keyboard handling, search filtering, and accessibility.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { Search, Terminal, ArrowRight, Clock } from 'lucide-react'

import { STATIC_COMMANDS, filterCommands, type StaticCommand } from '@/shared/lib/commands'
import { useUIStore } from '@/shared/stores/ui-store'
import { useShortcutStore } from '@/shared/stores/shortcut-store'
import { useCommandPaletteStore, type RecentItem } from '@/shared/stores/command-palette-store'
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from '@/shared/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'

/**
 * Main CommandPalette component.
 */
export function CommandPalette() {
  const navigate = useNavigate()
  const [query, setQuery] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Store state
  const open = useUIStore((s) => s.commandPaletteOpen)
  const setOpen = useUIStore((s) => s.setCommandPaletteOpen)
  const toggleSessionPanel = useUIStore((s) => s.toggleSessionPanel)
  const setOverlayOpen = useShortcutStore((s) => s.setOverlayOpen)

  // Recent items
  const recentItems = useCommandPaletteStore((s) => s.recentItems)
  const addRecentItem = useCommandPaletteStore((s) => s.addRecentItem)

  // Filter commands by query
  const filteredCommands = React.useMemo(
    () => filterCommands(STATIC_COMMANDS, query),
    [query],
  )

  // Handle command selection
  const handleCommandSelect = React.useCallback(
    (command: StaticCommand) => {
      if (command.action === 'toggle-session-panel') {
        toggleSessionPanel()
        setOpen(false)
        return
      }

      if (command.action === 'show-shortcuts') {
        setOpen(false)
        // Small delay to let palette close first
        setTimeout(() => setOverlayOpen(true), 100)
        return
      }

      // Navigation commands take precedence over generic action dispatch
      if (command.href) {
        addRecentItem({
          id: command.id,
          type: 'navigation',
          label: command.label,
          url: command.href,
        })
        navigate(command.href)
        setOpen(false)
        return
      }

      // Generic action dispatch — emit a custom event for feature modules to handle.
      // This powers action: commands like csv exports without coupling features to the palette.
      if (command.action) {
        window.dispatchEvent(new CustomEvent('stream:action', { detail: { action: command.action } }))
        setOpen(false)
        return
      }
    },
    [navigate, setOpen, toggleSessionPanel, setOverlayOpen, addRecentItem],
  )

  // Handle recent item selection
  const handleRecentSelect = React.useCallback(
    (item: RecentItem) => {
      if (item.url) {
        addRecentItem({
          id: item.id,
          type: item.type,
          label: item.label,
          url: item.url,
        })
        navigate(item.url)
        setOpen(false)
      }
    },
    [navigate, setOpen, addRecentItem],
  )

  // Reset query when dialog closes
  React.useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  // Focus input when dialog opens
  React.useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  // Check if we have any results
  const hasResults = recentItems.length > 0 || filteredCommands.length > 0
  const showEmptyState = query.trim() && !hasResults

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          style={styles.content}
          className="command-palette-content"
        >
          <DialogPrimitive.Title style={styles.srOnly}>Command Palette</DialogPrimitive.Title>
          <Command
            style={styles.command}
            shouldFilter={false}
            loop
          >
            {/* Search Input */}
            <div style={styles.searchWrapper}>
              <Search style={styles.searchIcon} />
              <Command.Input
                ref={inputRef}
                value={query}
                onValueChange={setQuery}
                placeholder="Type a command or search..."
                style={styles.searchInput}
                data-testid="command-palette-input"
              />
            </div>

            {/* Results List */}
            <Command.List style={styles.list}>
              {/* Empty State */}
              {showEmptyState && (
                <Command.Empty style={styles.empty}>
                  No results found.
                </Command.Empty>
              )}

              {/* RECENT Section */}
              {recentItems.length > 0 && !query.trim() && (
                <Command.Group
                  heading={
                    <span style={styles.groupHeading}>Recent</span>
                  }
                >
                  {recentItems.map((item) => (
                    <Command.Item
                      key={item.id}
                      value={`recent-${item.id}`}
                      onSelect={() => handleRecentSelect(item)}
                      style={styles.item}
                      className="command-item"
                      data-testid={`command-palette-item-recent-${item.id}`}
                    >
                      <span style={styles.itemIconWrapper}>
                        <Clock style={styles.itemIcon} />
                      </span>
                      <span style={styles.itemLabel}>{item.label}</span>
                      <ArrowRight style={styles.itemArrow} />
                    </Command.Item>
                  ))}
                </Command.Group>
              )}

              {/* COMMANDS Section */}
              {filteredCommands.length > 0 && (
                <Command.Group
                  heading={
                    <span style={styles.groupHeading}>Commands</span>
                  }
                >
                  {filteredCommands.map((cmd) => (
                    <Command.Item
                      key={cmd.id}
                      value={`command-${cmd.id}`}
                      onSelect={() => handleCommandSelect(cmd)}
                      style={styles.item}
                      className="command-item"
                      data-testid={`command-palette-item-${cmd.id}`}
                    >
                      <span style={styles.chordBadge}>
                        {cmd.chord}
                      </span>
                      <span style={styles.itemLabelFlex}>{cmd.label}</span>
                      <Terminal style={styles.itemArrow} />
                    </Command.Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

export default CommandPalette

const styles: Record<string, CSSProperties> = {
  content: {
    position: 'fixed',
    left: '50%',
    top: '20%',
    zIndex: 50,
    width: '100%',
    maxWidth: '576px',
    transform: 'translateX(-50%)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    boxShadow: 'var(--shadow-lg)',
  },
  command: {
    display: 'flex',
    flexDirection: 'column',
  },
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-default)',
    padding: '0 12px',
  },
  searchIcon: {
    marginRight: '8px',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: 'var(--text-secondary)',
  },
  searchInput: {
    display: 'flex',
    height: '44px',
    width: '100%',
    background: 'transparent',
    padding: '12px 0',
    fontSize: '14px',
    outline: 'none',
    border: 'none',
    color: 'var(--text-primary)',
  },
  list: {
    maxHeight: '300px',
    overflow: 'auto',
    padding: '8px',
  },
  empty: {
    padding: '24px 0',
    textAlign: 'center',
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  groupHeading: {
    padding: '0 8px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  item: {
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    gap: '8px',
    borderRadius: 'var(--radius-sm)',
    padding: '8px',
    fontSize: '14px',
    outline: 'none',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: 'var(--text-primary)',
  },
  itemIconWrapper: {
    display: 'flex',
    width: '20px',
    height: '20px',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
  },
  itemIcon: {
    width: '16px',
    height: '16px',
  },
  itemLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemLabelFlex: {
    flex: 1,
  },
  itemArrow: {
    width: '12px',
    height: '12px',
    color: 'var(--text-secondary)',
  },
  chordBadge: {
    display: 'flex',
    width: '20px',
    height: '20px',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    background: 'var(--bg-tertiary)',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}
